import React, { useMemo } from "react";
import _ from "lodash";
import { AddStaffIllustration } from "@toddle-edu/ds-theme";

import CohortSubText from "./components/CohortSubText";
import { compose } from "lib/apolloClient";

import withLoader from "UIComponents/GqlWithLoader";
import DualPanelManageMembers from "AppComponents/DualPanelManageMembersV2";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import {
  getCourseUsersQueryV3,
  getGradeUsersQueryV3,
  getOrganizationCourseUsersQueryV3,
  getMessageV2UsersWithStudentParentQueryV3,
} from "MessagingV2/modules/MessagingQuery";
import { getOrganizationId, getUserId } from "modules/CommonSelectors";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import {
  COHORT_TYPE,
  DUAL_PANEL_VIEW_MODE,
  HEADER_TYPE,
} from "AppComponents/DualPanelManageMembersV2/module/utils";
import {
  CHAT_STATUS_ENUM,
  isMessagingRnpEnabled,
  isCreateChatsAndChannelsEnableForAll,
  RNP_CAN_CREATE_AND_MANAGE_CHATS_AND_CHANNELS_FOR_CLASS,
} from "MessagingV2/modules/Utils";
import { createCurriculumBasedQueryEnhancer } from "MessagingV2/modules/MessagingEnhancers";
import { FILTER_KEYS } from "../Channels/CreateChannelModal/Filter";

const getSelectedUserTypes = ownProps => {
  return _.get(
    ownProps,
    ["customQueryWrapperFields", "selectedFilters", FILTER_KEYS.USER_TYPE],
    []
  );
};

const getIncludeFlags = selectedUserTypes => ({
  includeStudents:
    _.isEmpty(selectedUserTypes) || _.includes(selectedUserTypes, STUDENT),
  includeStaff:
    _.isEmpty(selectedUserTypes) || _.includes(selectedUserTypes, STAFF),
  includeParents:
    _.isEmpty(selectedUserTypes) || _.includes(selectedUserTypes, PARENT),
});

const getMessagingCoursesCohorts = (edges, selectedUserTypes) => {
  const { includeStudents, includeStaff, includeParents } = getIncludeFlags(
    selectedUserTypes
  );
  const coursesCohorts = [];
  _.forEach(edges, course => {
    const {
      students = {},
      collaborator = {},
      familyMembers = {},
      id,
      title,
      grades = [],
    } = _.get(course, "node", {});
    if (includeStudents) {
      const studentsList = _.map(_.get(students, "edges", []), "node");
      coursesCohorts.push({
        id,
        title,
        type: STUDENT,
        userList: studentsList,
        grades,
      });
    }
    if (includeStaff) {
      const collaboratorList = _.map(_.get(collaborator, "edges", []), "node");
      coursesCohorts.push({
        id,
        title,
        type: STAFF,
        userList: collaboratorList,
        grades,
      });
    }
    if (includeParents) {
      const familyMembersList = _.map(
        _.get(familyMembers, "edges", []),
        "node"
      );
      coursesCohorts.push({
        id,
        title,
        type: PARENT,
        userList: familyMembersList,
        grades,
      });
    }
  });
  return coursesCohorts;
};

const getMessagingGradesCohorts = (edges, selectedUserTypes) => {
  const { includeStudents, includeStaff, includeParents } = getIncludeFlags(
    selectedUserTypes
  );
  const gradeCohorts = [];
  _.forEach(edges, gradeItem => {
    const grade = _.get(gradeItem, "node", gradeItem);
    const {
      allStudents = [],
      allStaffs = [],
      allParents = [],
      id,
      name: title,
    } = grade;
    if (includeStudents) {
      gradeCohorts.push({ id, title, type: STUDENT, userList: allStudents });
    }
    if (includeStaff) {
      gradeCohorts.push({ id, title, type: STAFF, userList: allStaffs });
    }
    if (includeParents) {
      gradeCohorts.push({ id, title, type: PARENT, userList: allParents });
    }
  });
  return gradeCohorts;
};

// If school filters are selected and this curriculum is not among them, skip it.
const isSchoolFiltered = (props, curriculumId) => {
  const schoolIds = _.get(
    props,
    ["customQueryWrapperFields", "selectedFilters", FILTER_KEYS.SCHOOL],
    []
  );
  return !_.isEmpty(schoolIds) && !_.includes(schoolIds, curriculumId);
};

const buildCurriculumHeader = curriculum => {
  return {
    type: HEADER_TYPE.SCHOOL,
    id: curriculum.id,
    title: curriculum.label,
  };
};

const buildComposedWrapper = () =>
  compose(
    // ── Members — one HOC per curriculum program ─────────────────────────────
    ...createCurriculumBasedQueryEnhancer({
      query: getMessageV2UsersWithStudentParentQueryV3,
      namePrefix: "getMessageV2UsersWithStudentParent",
      skip: (props, curriculum) => {
        if (isSchoolFiltered(props, curriculum.id)) return true;
        const typeFilter = props.customQueryWrapperFields?.typeFilter;
        return !!typeFilter && typeFilter !== DUAL_PANEL_VIEW_MODE.MEMBERS;
      },
      getVariables: (props, curriculum) => {
        const selectedUserTypes = _.get(
          props,
          [
            "customQueryWrapperFields",
            "selectedFilters",
            FILTER_KEYS.USER_TYPE,
          ],
          []
        );
        return {
          organizationId: getOrganizationId(),
          portalType: "PLANNER",
          input: {
            first: 15,
            userType: _.isEmpty(selectedUserTypes)
              ? [STAFF, STUDENT, PARENT]
              : selectedUserTypes,
            searchText: props.searchText,
            userStatus: "ACTIVE",
            chatStatus: CHAT_STATUS_ENUM.ALL,
            includePaidUsersOnly: false,
            includeInitiatedChatUsers: false,
            curriculumProgramIds: [curriculum.id],
          },
        };
      },
      edgesPath: "messageV2Users.edges",
      pageInfoPath: "messageV2Users.pageInfo",
      totalCountPath: "messageV2Users.totalCount",
      transformEdges: edges => edges,
      cohortType: COHORT_TYPE.MEMBERS,
      buildHeaderParams: buildCurriculumHeader,
      loadMoreKey: "messageV2Users",
      buildLoadMoreVariables: (queryVariables, endCursor) => ({
        ...queryVariables,
        input: {
          ...queryVariables.input,
          before: endCursor,
        },
      }),
    }),

    // ── Classes (admin path) — one HOC per curriculum program ─────────────────
    ...createCurriculumBasedQueryEnhancer({
      query: getOrganizationCourseUsersQueryV3,
      namePrefix: "getOrgCourseUsers",
      skip: (props, curriculum) => {
        if (isMessagingRnpEnabled() || !getIsAdminByRole()) return true;
        if (isSchoolFiltered(props, curriculum.id)) return true;
        const typeFilter = props.customQueryWrapperFields?.typeFilter;
        return !!typeFilter && typeFilter !== DUAL_PANEL_VIEW_MODE.CLASSES;
      },
      getVariables: (props, curriculum) => {
        const selectedUserTypes = _.get(
          props,
          [
            "customQueryWrapperFields",
            "selectedFilters",
            FILTER_KEYS.USER_TYPE,
          ],
          []
        );
        return {
          organizationId: getOrganizationId(),
          first: 15,
          portalType: "PLANNER",
          filters: {
            searchText: props.searchText,
            curriculumProgramIds: [curriculum.id],
          },
          ...getIncludeFlags(selectedUserTypes),
        };
      },
      edgesPath: "courses.edges",
      pageInfoPath: "courses.pageInfo",
      totalCountPath: "courses.totalCount",
      transformEdges: (edges, ownProps) =>
        getMessagingCoursesCohorts(edges, getSelectedUserTypes(ownProps)),
      cohortType: COHORT_TYPE.CLASSES,
      buildHeaderParams: buildCurriculumHeader,
      loadMoreKey: "courses",
    }),

    // ── Classes (RnP path) — one HOC per curriculum program ───────────────────
    ...createCurriculumBasedQueryEnhancer({
      query: getCourseUsersQueryV3,
      namePrefix: "getCourseUsers",
      skip: (props, curriculum) => {
        if (!isMessagingRnpEnabled() && getIsAdminByRole()) return true;
        if (isSchoolFiltered(props, curriculum.id)) return true;
        const typeFilter = props.customQueryWrapperFields?.typeFilter;
        return !!typeFilter && typeFilter !== DUAL_PANEL_VIEW_MODE.CLASSES;
      },
      getVariables: (props, curriculum) => {
        const selectedUserTypes = _.get(
          props,
          [
            "customQueryWrapperFields",
            "selectedFilters",
            FILTER_KEYS.USER_TYPE,
          ],
          []
        );
        return {
          id: getUserId(),
          organizationId: getOrganizationId(),
          first: 15,
          portalType: "PLANNER",
          filters: {
            searchText: props.searchText,
            curriculumProgramIds: [curriculum.id],
            ...(isMessagingRnpEnabled()
              ? {
                  fetchOnlyMappedCourses: false,
                  controlParameters: [
                    RNP_CAN_CREATE_AND_MANAGE_CHATS_AND_CHANNELS_FOR_CLASS,
                  ],
                }
              : {}),
          },
          ...getIncludeFlags(selectedUserTypes),
        };
      },
      edgesPath: "coursesV2.edges",
      pageInfoPath: "coursesV2.pageInfo",
      totalCountPath: "coursesV2.totalCount",
      transformEdges: (edges, ownProps) =>
        getMessagingCoursesCohorts(edges, getSelectedUserTypes(ownProps)),
      cohortType: COHORT_TYPE.CLASSES,
      buildHeaderParams: buildCurriculumHeader,
      loadMoreKey: "coursesV2",
    }),

    // ── Grades — one HOC per curriculum program ────────────────────────────────
    ...createCurriculumBasedQueryEnhancer({
      query: getGradeUsersQueryV3,
      namePrefix: "getGradeUsers",
      skip: (props, curriculum) => {
        if (isMessagingRnpEnabled() && !isCreateChatsAndChannelsEnableForAll())
          return true;
        if (isSchoolFiltered(props, curriculum.id)) return true;
        const typeFilter = props.customQueryWrapperFields?.typeFilter;
        return !!typeFilter && typeFilter !== DUAL_PANEL_VIEW_MODE.GRADES;
      },
      getVariables: (props, curriculum) => {
        const selectedUserTypes = _.get(
          props,
          [
            "customQueryWrapperFields",
            "selectedFilters",
            FILTER_KEYS.USER_TYPE,
          ],
          []
        );
        return {
          organizationId: getOrganizationId(),
          first: 15,
          portalType: "PLANNER",
          filters: {
            searchText: props.searchText,
            curriculumProgramIds: [curriculum.id],
          },
          ...getIncludeFlags(selectedUserTypes),
        };
      },
      edgesPath: "gradesV2.edges",
      pageInfoPath: "gradesV2.pageInfo",
      totalCountPath: "gradesV2.totalCount",
      transformEdges: (edges, ownProps) =>
        getMessagingGradesCohorts(edges, getSelectedUserTypes(ownProps)),
      cohortType: COHORT_TYPE.GRADES,
      buildHeaderParams: buildCurriculumHeader,
      loadMoreKey: "gradesV2",
    }),
    withLoader
  )(DualPanelManageMembers.CohortList);

const DualPanelQueryWrapper = props => {
  // Build the composed component once per mount so that
  // getUserCurriculumPrograms() is called with live store data,
  // not at module-load time when the store may not be hydrated yet.
  const ComposedWrapper = useMemo(() => buildComposedWrapper(), []);
  return <ComposedWrapper {...props} />;
};

const SEARCH_PLACEHOLDER_MAP = {
  [DUAL_PANEL_VIEW_MODE.MEMBERS]: "announcement:search_members",
  [DUAL_PANEL_VIEW_MODE.CLASSES]: "announcement:search_classes",
  [DUAL_PANEL_VIEW_MODE.GRADES]: "announcement:search_grades",
};

export const renderCohortSubText = ({ cohort, type, t }) => (
  <CohortSubText cohort={cohort} type={type} t={t} />
);

export const metadataPropsV2 = {
  emptyStateSubtitle: "common:add_members_v3_search_empty_state_subtitle",
  countUsersSelectedText: "common:count_member_selected",
  selectedUsersListText: "common:selected_members_list",
  disabledUserTooltipText: "common:user_already_exists_in_channel",
  disabledNestedFamilyTooltipText:
    "common:family_member_already_exist_in_channel",
  viewMembersEmptyStateTitle: "common:no_members_have_been_selected_yet",
  viewMembersEmptyStateSubtitle: "common:select_members_to_view_here",
  viewMembersEmptyStateIllustration: AddStaffIllustration,
  getSearchPlaceholder: ({ t, customQueryWrapperFields }) => {
    const typeFilter = _.get(customQueryWrapperFields, "typeFilter");
    const customPlaceholder = _.get(SEARCH_PLACEHOLDER_MAP, [typeFilter]);
    return customPlaceholder
      ? t(customPlaceholder)
      : t("common:search_placeholder");
  },
  renderCohortSubText,
};

export default DualPanelQueryWrapper;
